/**
 * Cribbage Clash 481 Track Variant Manager
 * Manages hazard definitions, obstacles, placement validation, collision math,
 * and stop sign / dice / wheel resolution.
 */

export const CLASH_OBSTACLE_TYPES = Object.freeze([
  'noPassingLanes',
  'jump',
  'jail',
  'bar',
  'detour',
  'roundAbout',
  'timeout',
  'stopSigns',
  'diceRoll',
  'spinningWheel'
]);

export const WHEEL_SLOTS = Object.freeze([
  'Score Plus x2',
  'Score Plus x3',
  'Plus 10',
  'Minus 10',
  'Go To Jail',
  'Go To The Bar',
  'Go To Timeout',
  'Roll The Dice'
]);

export const SIGN_PRIORITY = Object.freeze([
  'Score Plus x3',
  'Score Plus x2',
  'Plus 10',
  'Plus 5',
  'Spin The Wheel',
  'Roll The Dice',
  'Undo',
  'Minus 5',
  'Score Minus x2',
  'Minus 10',
  'Score Minus x3',
  'Go To The Bar',
  'Go To Timeout',
  'Go To Jail'
]);

export const SIGN_DEFS = Object.freeze({
  'Plus 5': { icon: '➕', desc: 'Advance your peg 5 holes forward' },
  'Plus 10': { icon: '⏩', desc: 'Advance your peg 10 holes forward' },
  'Minus 5': { icon: '➖', desc: 'Move your peg back 5 holes' },
  'Minus 10': { icon: '⏪', desc: 'Move your peg back 10 holes' },
  'Score Plus x2': { icon: '✖️2️⃣', desc: 'Double your entire current score!' },
  'Score Plus x3': { icon: '✖️3️⃣', desc: 'Triple your entire current score!' },
  'Score Minus x2': { icon: '➗2️⃣', desc: 'Cut your entire current score in half' },
  'Score Minus x3': { icon: '➗3️⃣', desc: 'Reduce your score to one-third' },
  'Undo': { icon: '↩️', desc: 'Undo the points that got you here' },
  'Go To Jail': { icon: '⛓️', desc: 'Trapped in Jail until you roll a pair' },
  'Go To The Bar': { icon: '🍺', desc: 'Enjoy drinks at The Bar until 15/31' },
  'Go To Timeout': { icon: '⏳', desc: 'Forced to wait in Timeout for 3 turns' },
  'Roll The Dice': { icon: '🎲', desc: 'Roll 3 dice for Direction, Pts & Target' },
  'Spin The Wheel': { icon: '🎡', desc: 'Spin the wheel with 8 prize/penalty slots' }
});

export const CLASH_DEFAULTS_481 = Object.freeze({
  noPassingLaneStarts: [40, 175, 275, 415],
  jumpStarts: [25, 195, 295, 435, 75, 145, 245, 365],
  jumpDests: [60, 160, 335, 395, 110, 125, 280, 400],
  jailHoles: [110, 315],
  barHoles: [125, 375],
  timeoutHoles: [75, 255],
  detourHoles: [15, 235],
  roundAboutHoles: [145, 355],
  signsStarts: [90, 215, 450, 465],
  stopSignHoles: [35, 70, 105, 155, 205, 265, 345, 425],
  diceHoles: [50, 185, 285, 410],
  wheelHoles: [65, 160, 260, 385],
  optDetour1Hole: 15,
  optJump1Start: 25,
  optNoPassingLane1Start: 40,
  optJump1Dest: 60,
  optTimeout1Hole: 75,
  optSigns1Hole: 90,
  optJail1Hole: 110,
  optBar1Hole: 125,
  optRoundAbout1Hole: 145,
  optJump2Dest: 160,
  optNoPassingLane2Start: 175,
  optJump2Start: 195,
  optSigns2Hole: 215,
  optDetour2Hole: 235,
  optTimeout2Hole: 255,
  optNoPassingLane3Start: 275,
  optJump3Start: 295,
  optJail2Hole: 315,
  optJump3Dest: 335,
  optRoundAbout2Hole: 355,
  optBar2Hole: 375,
  optJump4Dest: 395,
  optNoPassingLane4Start: 415,
  optJump4Start: 435,
  optJump5Start: 75,
  optJump5Dest: 110,
  optJump6Start: 145,
  optJump6Dest: 125,
  optJump7Start: 245,
  optJump7Dest: 280,
  optJump8Start: 365,
  optJump8Dest: 400,
  optSigns3Hole: 450,
  optSigns4Hole: 465,
  optStopSign1Hole: 35,
  optStopSign2Hole: 70,
  optStopSign3Hole: 105,
  optStopSign4Hole: 155,
  optStopSign5Hole: 205,
  optStopSign6Hole: 265,
  optStopSign7Hole: 345,
  optStopSign8Hole: 425,
  optDice1Hole: 50,
  optDice2Hole: 185,
  optDice3Hole: 285,
  optDice4Hole: 410,
  optWheel1Hole: 65,
  optWheel2Hole: 160,
  optWheel3Hole: 260,
  optWheel4Hole: 385,
  optSkunkMarkerHole: 360,
  optDoubleSkunkMarkerHole: 240
});

export const CLASH_DEFAULTS_241 = Object.freeze({
  noPassingLaneStarts: [40, 95, 150, 195],
  jumpStarts: [25, 115, 145, 205, 55, 85, 135, 185],
  jumpDests: [60, 80, 185, 170, 75, 65, 160, 210],
  jailHoles: [70, 175],
  barHoles: [85, 190],
  timeoutHoles: [45, 140],
  detourHoles: [15, 125],
  roundAboutHoles: [65, 165],
  signsStarts: [50, 105, 155, 215],
  stopSignHoles: [25, 50, 75, 100, 125, 150, 175, 200],
  diceHoles: [30, 60, 130, 180],
  wheelHoles: [40, 80, 150, 200],
  optDetour1Hole: 15,
  optJump1Start: 25,
  optNoPassingLane1Start: 40,
  optJump1Dest: 60,
  optTimeout1Hole: 45,
  optSigns1Hole: 50,
  optJail1Hole: 70,
  optBar1Hole: 85,
  optRoundAbout1Hole: 65,
  optJump2Dest: 80,
  optNoPassingLane2Start: 95,
  optJump2Start: 115,
  optSigns2Hole: 105,
  optDetour2Hole: 125,
  optTimeout2Hole: 140,
  optNoPassingLane3Start: 150,
  optJump3Start: 145,
  optJail2Hole: 175,
  optJump3Dest: 185,
  optRoundAbout2Hole: 165,
  optBar2Hole: 190,
  optJump4Dest: 170,
  optNoPassingLane4Start: 195,
  optJump4Start: 205,
  optJump5Start: 55,
  optJump5Dest: 75,
  optJump6Start: 85,
  optJump6Dest: 65,
  optJump7Start: 135,
  optJump7Dest: 160,
  optJump8Start: 185,
  optJump8Dest: 210,
  optSigns3Hole: 155,
  optSigns4Hole: 215,
  optStopSign1Hole: 25,
  optStopSign2Hole: 50,
  optStopSign3Hole: 75,
  optStopSign4Hole: 100,
  optStopSign5Hole: 125,
  optStopSign6Hole: 150,
  optStopSign7Hole: 175,
  optStopSign8Hole: 200,
  optDice1Hole: 30,
  optDice2Hole: 60,
  optDice3Hole: 130,
  optDice4Hole: 180,
  optWheel1Hole: 40,
  optWheel2Hole: 80,
  optWheel3Hole: 150,
  optWheel4Hole: 200,
  optSkunkMarkerHole: 180,
  optDoubleSkunkMarkerHole: 120
});

export const CLASH_DEFAULTS_361 = Object.freeze({
  noPassingLaneStarts: [40, 135, 215, 315],
  jumpStarts: [25, 145, 225, 325, 65, 105, 185, 275],
  jumpDests: [60, 115, 260, 295, 85, 95, 210, 310],
  jailHoles: [90, 245],
  barHoles: [105, 285],
  timeoutHoles: [65, 195],
  detourHoles: [15, 175],
  roundAboutHoles: [115, 265],
  signsStarts: [70, 165, 255, 335],
  stopSignHoles: [30, 65, 105, 150, 195, 240, 285, 330],
  diceHoles: [50, 140, 230, 320],
  wheelHoles: [70, 160, 250, 330],
  optDetour1Hole: 15,
  optJump1Start: 25,
  optNoPassingLane1Start: 40,
  optJump1Dest: 60,
  optTimeout1Hole: 65,
  optSigns1Hole: 70,
  optJail1Hole: 90,
  optBar1Hole: 105,
  optRoundAbout1Hole: 115,
  optJump2Dest: 115,
  optNoPassingLane2Start: 135,
  optJump2Start: 145,
  optSigns2Hole: 165,
  optDetour2Hole: 175,
  optTimeout2Hole: 195,
  optNoPassingLane3Start: 215,
  optJump3Start: 225,
  optJail2Hole: 245,
  optJump3Dest: 260,
  optRoundAbout2Hole: 265,
  optBar2Hole: 285,
  optJump4Dest: 295,
  optNoPassingLane4Start: 315,
  optJump4Start: 325,
  optJump5Start: 65,
  optJump5Dest: 85,
  optJump6Start: 105,
  optJump6Dest: 95,
  optJump7Start: 185,
  optJump7Dest: 210,
  optJump8Start: 275,
  optJump8Dest: 310,
  optSigns3Hole: 255,
  optSigns4Hole: 335,
  optStopSign1Hole: 30,
  optStopSign2Hole: 65,
  optStopSign3Hole: 105,
  optStopSign4Hole: 150,
  optStopSign5Hole: 195,
  optStopSign6Hole: 240,
  optStopSign7Hole: 285,
  optStopSign8Hole: 330,
  optDice1Hole: 50,
  optDice2Hole: 140,
  optDice3Hole: 230,
  optDice4Hole: 320,
  optWheel1Hole: 70,
  optWheel2Hole: 160,
  optWheel3Hole: 250,
  optWheel4Hole: 330,
  optSkunkMarkerHole: 270,
  optDoubleSkunkMarkerHole: 180
});

export function getClashDefaults(target) {
  const t = Number(target) || 481;
  if (t === 241) return CLASH_DEFAULTS_241;
  if (t === 361) return CLASH_DEFAULTS_361;
  return CLASH_DEFAULTS_481;
}

export function getSkunkDefaults(target) {
  const t = Number(target) || 481;
  const track = t - 1;
  return {
    skunkHole: Math.round(track * 0.75),
    doubleSkunkHole: Math.round(track * 0.5)
  };
}

export class ClashManager {
  constructor(options = {}) {
    this.target = Number(options.target || 481);
    this.options = { ...options };
  }

  isClashMode(target = this.target) {
    return [241, 361, 481].includes(Number(target));
  }

  static getClashDefaults(target) {
    return getClashDefaults(target);
  }

  getClashDefaults(target = this.target) {
    return getClashDefaults(target);
  }

  static getSkunkDefaults(target) {
    return getSkunkDefaults(target);
  }

  getSkunkDefaults(target = this.target) {
    return getSkunkDefaults(target);
  }

  /**
   * Evaluates collision/overlap between candidate start/length and existing active obstacles.
   * @param {number} candidateStart
   * @param {number} length
   * @param {string|null} excludeSelectId
   * @param {Array} blockList
   * @returns {Object|null} The conflicting obstacle, or null if free
   */
  static checkCollision(candidateStart, length, excludeSelectId, blockList) {
    if (!Array.isArray(blockList)) return null;
    const cEnd = candidateStart + length - 1;
    for (const b of blockList) {
      if (b.selectId && excludeSelectId && b.selectId === excludeSelectId) continue;
      if (Math.max(candidateStart, b.start) <= Math.min(cEnd, b.end)) {
        return b;
      }
    }
    return null;
  }

  checkCollision(candidateStart, length, excludeSelectId, blockList) {
    return ClashManager.checkCollision(candidateStart, length, excludeSelectId, blockList);
  }

  /**
   * Finds the nearest available non-colliding hole from a list of valid choices.
   * @param {Array<number>} options Available hole options
   * @param {number} currentVal Currently desired or selected value
   * @param {number} length Length of the obstacle
   * @param {string|null} excludeSelectId
   * @param {Array} activeBlocks
   * @returns {number}
   */
  static findAvailableHole(options, currentVal, length, excludeSelectId, activeBlocks) {
    if (!ClashManager.checkCollision(currentVal, length, excludeSelectId, activeBlocks)) {
      return currentVal;
    }
    const validOptions = options.filter(h => !ClashManager.checkCollision(h, length, excludeSelectId, activeBlocks));
    if (validOptions.length > 0) {
      validOptions.sort((a, b) => Math.abs(a - currentVal) - Math.abs(b - currentVal));
      return validOptions[0];
    }
    return currentVal;
  }

  findAvailableHole(options, currentVal, length, excludeSelectId, activeBlocks) {
    return ClashManager.findAvailableHole(options, currentVal, length, excludeSelectId, activeBlocks);
  }

  /**
   * Decides which sign an AI player picks from the remaining available signs.
   * @param {Array<string>} remainingSigns
   * @returns {string}
   */
  static pickCpuSign(remainingSigns) {
    if (!Array.isArray(remainingSigns) || remainingSigns.length === 0) return 'Plus 5';
    for (const p of SIGN_PRIORITY) {
      if (remainingSigns.includes(p)) {
        return p;
      }
    }
    return remainingSigns[0];
  }

  pickCpuSign(remainingSigns) {
    return ClashManager.pickCpuSign(remainingSigns);
  }

  /**
   * Evaluates the mathematical score delta or special action for a selected sign.
   * @param {string} signName
   * @param {number} currentScore
   * @param {number} lastDelta
   * @returns {{ type: 'score'|'action', delta?: number, action?: string }}
   */
  static evaluateSignEffect(signName, currentScore = 0, lastDelta = 2) {
    const cur = Math.max(0, parseInt(currentScore, 10) || 0);
    const delta = Math.abs(parseInt(lastDelta, 10) || 2);

    switch (signName) {
      case 'Plus 5':
        return { type: 'score', delta: 5 };
      case 'Plus 10':
        return { type: 'score', delta: 10 };
      case 'Minus 5':
        return { type: 'score', delta: -5 };
      case 'Minus 10':
        return { type: 'score', delta: -10 };
      case 'Score Plus x2':
        return { type: 'score', delta: cur };
      case 'Score Plus x3':
        return { type: 'score', delta: cur * 2 };
      case 'Score Minus x2':
        return { type: 'score', delta: -Math.floor(cur / 2) };
      case 'Score Minus x3':
        return { type: 'score', delta: -Math.floor(cur * 2 / 3) };
      case 'Undo':
        return { type: 'score', delta: -delta };
      case 'Go To Jail':
        return { type: 'action', action: 'jail' };
      case 'Go To The Bar':
        return { type: 'action', action: 'bar' };
      case 'Go To Timeout':
        return { type: 'action', action: 'timeout' };
      case 'Roll The Dice':
        return { type: 'action', action: 'dice' };
      case 'Spin The Wheel':
        return { type: 'action', action: 'wheel' };
      default:
        return { type: 'score', delta: 0 };
    }
  }

  evaluateSignEffect(signName, currentScore, lastDelta) {
    return ClashManager.evaluateSignEffect(signName, currentScore, lastDelta);
  }

  /**
   * Resolves the result of a 3-die roll.
   * @param {string} dir '+' or '−'
   * @param {number} val Points (5, 10, 15, 20, 25, 30)
   * @param {string} target 'You' | 'Opponent' | 'You Pick'
   * @returns {{ points: number, target: string }}
   */
  static evaluateDiceRoll(dir, val, target) {
    const multiplier = dir === '−' ? -1 : 1;
    const pts = Math.abs(parseInt(val, 10) || 5) * multiplier;
    return {
      points: pts,
      target: target || 'You'
    };
  }

  /**
   * Resolves the outcome of spinning the wheel.
   * @param {number} slotIndex 0 to 7
   * @returns {string} One of WHEEL_SLOTS
   */
  static evaluateWheelSpin(slotIndex) {
    const idx = Math.max(0, Math.min(WHEEL_SLOTS.length - 1, parseInt(slotIndex, 10) || 0));
    return WHEEL_SLOTS[idx];
  }
}
